/* eslint-disable react/forbid-prop-types */
// prop-type is a library for typechecking of props
/*
import { ErrorMessage } from "formik";
*/

import { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { ErrorMessage } from "formik";

import { InputLabel, Switch, Grid, Autocomplete } from "@mui/material";
import MDBox from "components/MDBox";
import FormField from "components/FormField";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import Map from "./Map";

function Form({ formData, locations, timezones, isEdit }) {
  const { formField, values, errors, touched, setFieldValue } = formData;
  const {
    description,
    location,
    timezone,
    area,
    project,
    status,
    mapCenter,
    mapZoom,
    profileImage,
    collectIP,
    signShift,
  } = formField;
  const {
    description: descriptionV,
    location: locationV,
    timezone: timezoneV,
    area: areaV,
    project: projectV,
    status: statusV,
    mapCenter: mapCenterV,
    mapZoom: mapZoomV,
    profileImage: profileImageV,
    collectIP: collectIPV,
    signShift: signShiftV,
  } = values;

  const locationValueOptionFormat = useMemo(() => {
    const lc = locations.find((locationItr) => locationItr.id === locationV);
    if (lc) {
      return {
        id: lc.id,
        label: lc.name,
      };
    }
    return null;
  }, [locations, locationV]);

  const timezoneValueOptionFormat = useMemo(() => {
    const tz = timezones.find((timezoneItr) => timezoneItr.id === timezoneV);
    if (tz) {
      return {
        id: tz.id,
        label: tz.text,
      };
    }
    return null;
  }, [timezones, timezoneV]);

  const locationOptions = useMemo(
    () =>
      locations.map((l) => ({
        label: l.name,
        id: l.id,
      })),
    [locations]
  );

  const timezoneOptions = useMemo(
    () =>
      timezones.map((l) => ({
        label: l.text,
        id: l.id,
      })),
    [timezones]
  );

  const statusValue = useMemo(() => {
    switch (typeof statusV === "string") {
      case "string": {
        return statusV === "1";
      }
      case "number": {
        return statusV === 1;
      }
      case "boolean": {
        return statusV;
      }
      default: {
        return !!statusV;
      }
    }
  }, [statusV]);

  const profileImageValue = useMemo(() => {
    switch (typeof profileImageV === "string") {
      case "string": {
        return profileImageV === "1";
      }
      case "number": {
        return profileImageV === 1;
      }
      case "boolean": {
        return profileImageV;
      }
      default: {
        return !!profileImageV;
      }
    }
  }, [profileImageV]);

  const collectIPValue = useMemo(() => {
    switch (typeof collectIPV === "string") {
      case "string": {
        return collectIPV === "1";
      }
      case "number": {
        return collectIPV === 1;
      }
      case "boolean": {
        return collectIPV;
      }
      default: {
        return !!collectIPV;
      }
    }
  }, [collectIPV]);

  const signShiftValue = useMemo(() => {
    switch (typeof signShiftV === "string") {
      case "string": {
        return signShiftV === "1";
      }
      case "number": {
        return signShiftV === 1;
      }
      case "boolean": {
        return signShiftV;
      }
      default: {
        return !!signShiftV;
      }
    }
  }, [signShiftV]);

  const onAreaChange = useCallback(
    (areaValue) => {
      setFieldValue(area.name, areaValue);
    },
    [area.name, setFieldValue]
  );

  const setMapCenter = useCallback(
    (newMapCenterValue) => {
      setFieldValue(mapCenter.name, newMapCenterValue);
    },
    [mapCenter.name, setFieldValue]
  );

  const setMapZoom = useCallback(
    (newMapZoomValue) => {
      setFieldValue(mapZoom.name, newMapZoomValue);
    },
    [mapZoom.name, setFieldValue]
  );

  return (
    <MDBox>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormField
            type={description.type}
            label={description.label}
            name={description.name}
            value={descriptionV}
            placeholder={description.placeholder}
            error={errors.description && touched.description}
            success={descriptionV.length > 0 && !errors.description}
          />
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            value={locationValueOptionFormat}
            options={locationOptions}
            getOptionLabel={(option) => (option ? option.label : "")}
            onChange={(e, value) => setFieldValue(location.name, value?.id, true)}
            isOptionEqualToValue={(option, value) => option?.id === value}
            renderInput={(params) => (
              <MDInput
                {...params}
                label={location.label}
                name={location.name}
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={errors.location && touched.location}
                success={locationV && locationV.length > 0 && !errors.location}
              />
            )}
          />
          <MDBox mt={0.75}>
            <MDTypography component="div" variant="caption" color="error" fontWeight="regular">
              <ErrorMessage
                name={location.name}
                render={(message) => {
                  if (typeof message === "object" && message !== null) {
                    return Object.values(message);
                  }
                  return message;
                }}
              />
            </MDTypography>
          </MDBox>
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            value={timezoneValueOptionFormat}
            options={timezoneOptions}
            getOptionLabel={(option) => (option ? option.label : "")}
            onChange={(e, value) => setFieldValue(timezone.name, value?.id, true)}
            isOptionEqualToValue={(option, value) => option.id === value}
            renderInput={(params) => (
              <MDInput
                {...params}
                label={timezone.label}
                name={timezone.name}
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={errors.timezone && touched.timezone}
                success={timezoneV && timezoneV.length > 0 && !errors.timezone}
              />
            )}
          />
          <MDBox mt={0.75}>
            <MDTypography component="div" variant="caption" color="error" fontWeight="regular">
              <ErrorMessage
                name={timezone.name}
                render={(message) => {
                  if (typeof message === "object" && message !== null) {
                    return Object.values(message);
                  }
                  return message;
                }}
              />
            </MDTypography>
          </MDBox>
        </Grid>

        <Grid item xs={12}>
          <Map
            name={area.name}
            value={areaV}
            onChange={onAreaChange}
            isEdit={isEdit}
            mapCenter={mapCenterV}
            mapZoom={mapZoomV}
            setMapCenter={setMapCenter}
            setMapZoom={setMapZoom}
          />
        </Grid>

        <Grid item xs={12}>
          <FormField
            type={project.type}
            label={project.label}
            name={project.name}
            value={projectV}
            placeholder={project.placeholder}
            error={errors.project && touched.project}
            success={projectV.length > 0 && !errors.project}
            inputProps={{ autoComplete: "" }}
          />
        </Grid>

        <Grid item xs={12}>
          <InputLabel>{status.label}</InputLabel>
          <Switch checked={statusValue} onChange={() => setFieldValue(status.name, !statusValue)} />
        </Grid>

        <Grid item xs={12}>
          <InputLabel>{profileImage.label}</InputLabel>
          <Switch
            checked={profileImageValue}
            onChange={() => setFieldValue(profileImage.name, !profileImageValue)}
          />
        </Grid>

        <Grid item xs={12}>
          <InputLabel>{collectIP.label}</InputLabel>
          <Switch
            checked={collectIPValue}
            onChange={() => setFieldValue(collectIP.name, !collectIPValue)}
          />
        </Grid>

        <Grid item xs={12}>
          <InputLabel>{signShift.label}</InputLabel>
          <Switch
            checked={signShiftValue}
            onChange={() => setFieldValue(signShift.name, !signShiftValue)}
          />
        </Grid>
      </Grid>
    </MDBox>
  );
}

// typechecking props for Form
Form.propTypes = {
  formData: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
  locations: PropTypes.arrayOf(PropTypes.object).isRequired,
  timezones: PropTypes.arrayOf(PropTypes.object).isRequired,
  isEdit: PropTypes.bool.isRequired,
};

export default Form;
